import React, { useState, useEffect, useMemo, lazy, Suspense, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from 'react-toastify';
import ImageUpload from "./ImageUpload";

// Import admin components
import SubcategoryManager from "./components/admin/SubcategoryManager";
import CRMTab from "./components/admin/CRMTab";
import ContentTab from "./components/admin/ContentTab";
import UsersTab from "./components/admin/UsersTab";
import ContentCategoriesTab from "./components/admin/ContentCategoriesTab";
import { AssessmentAnalytics, ContentActivityAnalytics, QuizLeadAnalytics } from "./components/admin/AnalyticsTab";
import AutoTagTool from "./components/admin/AutoTagTool";
import MarketplaceTab, { TrackingTab } from "./components/admin/MarketplaceTab";
import CouponsTab from "./components/admin/CouponsTab";
import HomepageCarouselTab from "./components/admin/HomepageCarouselTab";
import ContentBoxesTab from "./components/admin/ContentBoxesTab";
import DosingDatabaseTab from "./components/admin/DosingDatabaseTab";
import ImageLibraryTab from "./components/admin/ImageLibraryTab";
import ModerationTab from "./components/admin/ModerationTab";
import ChatRoomsTab from "./components/admin/ChatRoomsTab";
import CommunityPersonasTab from "./components/admin/CommunityPersonasTab";
import ReferralsTab from "./components/admin/ReferralsTab";
import SupportRequestsTab from "./components/admin/SupportRequestsTab";
import FAQTab from "./components/admin/FAQTab";
import { DashboardTab } from "./components/admin/DashboardTab";
import { HomePageTab } from "./components/admin/HomePageTab";
import { EventsTab } from "./components/admin/EventsTab";
import { SurveysTab } from "./components/admin/SurveysTab";
import { PollsTab } from "./components/admin/PollsTab";
import { QuizzesTab } from "./components/admin/QuizzesTab";

// Import icons
import { Users, BookOpen, BarChart3, Settings, Plus, Edit, Trash2, Eye, EyeOff, Save, X, Download, Upload, Image, Search, LayoutGrid, Link, Calculator, Shield, AlertTriangle, AlertCircle, MessageCircle, MessageSquare, ShoppingBag, Archive, RotateCcw, MousePointerClick, CheckCircle, Mail, Send, Filter, Calendar, TrendingUp, Lock, FileText, ArrowLeft, Copy, List, Star, Tag } from "lucide-react";

// Lazy load ReactQuill (React 19 compatible version) to avoid SSR issues and reduce initial bundle size
const ReactQuill = lazy(() => import('react-quill-new'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to construct proper image URL - used across all components
const getImageUrl = (url) => {
  if (!url) return '';
  // If URL is already absolute (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If URL starts with /api, prepend BACKEND_URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise, prepend API
  return `${API}${url}`;
};

// Collapsible Navigation Section Component
const NavSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-burgundy hover:bg-burgundy/5 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span>{title}</span>
        </div>
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="mt-1 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
};

// Navigation Item Component
const NavItem = ({ value, icon: Icon, label, activeTab, setActiveTab, badge = null }) => {
  const isActive = activeTab === value;
  
  return (
    <button
      onClick={() => setActiveTab(value)}
      className={`flex items-center justify-between w-full px-4 py-2.5 text-sm rounded-lg transition-all ${
        isActive
          ? 'bg-burgundy text-cream font-medium shadow-sm'
          : 'text-burgundy/70 hover:bg-burgundy/5 hover:text-burgundy'
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      {badge && badge > 0 && (
        <Badge className="h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
          {badge}
        </Badge>
      )}
    </button>
  );
};

// Admin Navigation Component
const AdminNavigation = ({ activeTab, setActiveTab, notificationCounts }) => {
  return (
    <div className="space-y-1">
      {/* Core Section */}
      <NavSection title="Core" icon={BarChart3} defaultOpen={true}>
        <NavItem value="dashboard" icon={BarChart3} label="Dashboard" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="tracking" icon={TrendingUp} label="Analytics" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="users" icon={Users} label="Users" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="referrals" icon={Users} label="Referrals" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Classroom Section */}
      <NavSection title="Classroom" icon={BookOpen} defaultOpen={true}>
        <NavItem value="content" icon={BookOpen} label="Content" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="categories" icon={List} label="Categories" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="auto-tag" icon={Tag} label="Auto-Tag" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="dosing" icon={Calculator} label="Dosing Database" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="image-library" icon={Image} label="Image Library" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Commerce Section */}
      <NavSection title="Commerce" icon={ShoppingBag} defaultOpen={false}>
        <NavItem value="marketplace" icon={ShoppingBag} label="Marketplace" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="coupons" icon={Archive} label="Coupons" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Website Section */}
      <NavSection title="Website" icon={LayoutGrid} defaultOpen={false}>
        <NavItem value="homepage" icon={Settings} label="Home Page" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="welcome-video" icon={LayoutGrid} label="Homepage Carousel" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="content-boxes" icon={LayoutGrid} label="Content Boxes" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="faq" icon={MessageCircle} label="FAQ" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Community Section */}
      <NavSection title="Community" icon={MessageSquare} defaultOpen={false}>
        <NavItem value="chat-rooms" icon={MessageCircle} label="Chat Rooms" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="community-personas" icon={Users} label="Seed Users" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="events" icon={Settings} label="Events" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem 
          value="moderation" 
          icon={Shield} 
          label="Moderation" 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          badge={notificationCounts.flagged_messages}
        />
        <NavItem 
          value="support-requests" 
          icon={MessageCircle} 
          label="Support Requests" 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          badge={notificationCounts.pending_support_requests}
        />
      </NavSection>

      {/* Marketing Section */}
      <NavSection title="Marketing" icon={Mail} defaultOpen={false}>
        <NavItem value="crm" icon={Mail} label="Email CRM" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Surveys & Polls Section */}
      <NavSection title="Feedback" icon={BarChart3} defaultOpen={false}>
        <NavItem value="surveys" icon={FileText} label="Surveys" activeTab={activeTab} setActiveTab={setActiveTab} />
        <NavItem value="polls" icon={BarChart3} label="Polls" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>

      {/* Quizzes Section */}
      <NavSection title="Learning" icon={BookOpen} defaultOpen={false}>
        <NavItem value="quizzes" icon={FileText} label="Quizzes" activeTab={activeTab} setActiveTab={setActiveTab} />
      </NavSection>
    </div>
  );
};

const AdminPanel = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [homePageContent, setHomePageContent] = useState({});
  const [communityTopics, setCommunityTopics] = useState([]);
  const [events, setEvents] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [categories, setCategories] = useState({
    content: ['perimenopause', 'nutrition', 'exercise', 'wellness'],
    community: ['general', 'perimenopause', 'exercise', 'nutrition', 'expert_qa'],
    events: ['workshops', 'webinars', 'coaching', 'community'],
    marketplace: ['body', 'mind', 'vitality']
  });
  const [loading, setLoading] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    flagged_messages: 0,
    pending_support_requests: 0,
    recent_warnings_issued: 0,
    total_notifications: 0
  });
  const [surveys, setSurveys] = useState([]);
  const [polls, setPolls] = useState([]);
  const [newPoll, setNewPoll] = useState({ question: '', options: ['', '', ''] });
  const [quizzes, setQuizzes] = useState([]);
  const [contentList, setContentList] = useState([]);

  useEffect(() => {
    fetchAdminStats();
    fetchUsers();
    fetchContent();
    fetchHomePageContent();
    fetchCommunityTopics();
    fetchEvents();
    fetchMarketplaceItems();
    fetchNotificationCounts();
    
    // Set up polling for notifications every 30 seconds
    const notificationInterval = setInterval(fetchNotificationCounts, 30000);
    return () => clearInterval(notificationInterval);
  }, []);

  const fetchNotificationCounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/notification-counts`);
      setNotificationCounts(response.data);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  };

  // Existing functions...
  const fetchAdminStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // New functions for community, events, marketplace
  const fetchCommunityTopics = async () => {
    try {
      const response = await axios.get(`${API}/admin/community-topics`);
      setCommunityTopics(response.data);
    } catch (error) {
      console.error('Error fetching community topics:', error);
      // Set default topics if API fails
      setCommunityTopics([
        { id: '1', name: 'General Discussion', description: 'Open discussion for all topics', active: true },
        { id: '2', name: 'Perimenopause Support', description: 'Support and advice for perimenopause journey', active: true },
        { id: '3', name: 'Exercise & Fitness', description: 'Workout tips and fitness motivation', active: true },
        { id: '4', name: 'Nutrition & Wellness', description: 'Healthy eating and nutrition advice', active: true },
        { id: '5', name: 'Expert Q&A', description: 'Ask questions to wellness experts', active: true }
      ]);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      // Set default events if API fails
      setEvents([
        { 
          id: '1', 
          title: 'Perimenopause Wellness Workshop', 
          description: 'Comprehensive workshop on navigating perimenopause',
          date: '2025-02-15',
          time: '10:00 AM',
          type: 'workshop',
          visible: true,
          capacity: 50
        }
      ]);
    }
  };

  const fetchMarketplaceItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/marketplace`);
      setMarketplaceItems(response.data);
    } catch (error) {
      console.error('Error fetching marketplace items:', error);
      // Set default items if API fails
      setMarketplaceItems([
        {
          id: '1',
          name: 'Premium Magnesium Supplement',
          description: 'High-quality magnesium for better sleep and relaxation',
          price: 29.99,
          category: 'body',
          subcategory: 'supplements',
          visible: true,
          link: 'https://luminellewellness.com/products/magnesium'
        }
      ]);
    }
  };

  const removeUser = async (userId) => {
    if (!confirm('Are you sure you want to remove this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}/permanent`);
      console.log('Delete response:', response.data);
      toast.success('User removed successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error removing user:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Error removing user';
      
      toast.error(`Failed to remove user: ${errorMessage}`);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchHomePageContent = async () => {
    try {
      const response = await axios.get(`${API}/home-content`);
      setHomePageContent(response.data);
    } catch (error) {
      console.error('Error fetching home page content:', error);
    }
  };

  const fetchSurveys = async (type = null) => {
    try {
      const url = type ? `${API}/admin/surveys?survey_type=${type}` : `${API}/admin/surveys`;
      const response = await axios.get(url);
      setSurveys(response.data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const fetchPolls = async () => {
    try {
      const response = await axios.get(`${API}/admin/polls`);
      setPolls(response.data);
    } catch (error) {
      console.error('Error fetching polls:', error);
    }
  };

  const handleCreatePoll = async () => {
    try {
      const validOptions = newPoll.options.filter(opt => opt.trim() !== '');
      if (!newPoll.question.trim() || validOptions.length < 2) {
        alert('Please provide a question and at least 2 options');
        return;
      }

      await axios.post(`${API}/admin/polls?admin_id=admin`, {
        question: newPoll.question,
        options: validOptions
      });

      alert('Poll created successfully!');
      setNewPoll({ question: '', options: ['', '', ''] });
      fetchPolls();
    } catch (error) {
      console.error('Error creating poll:', error);
      alert('Error creating poll');
    }
  };

  const handleTogglePoll = async (pollId) => {
    try {
      await axios.patch(`${API}/admin/polls/${pollId}/toggle`);
      fetchPolls();
    } catch (error) {
      console.error('Error toggling poll:', error);
    }
  };

  const handleExportSurveys = (type = null) => {
    const url = type 
      ? `${API}/admin/surveys/export?survey_type=${type}`
      : `${API}/admin/surveys/export`;
    window.open(url, '_blank');
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quizzes`);
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchContentForQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/admin/content`);
      setContentList(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;
    
    try {
      await axios.delete(`${API}/admin/quizzes/${quizId}`);
      fetchQuizzes();
      alert('Quiz deleted successfully');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Error deleting quiz');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-warm-white to-gold-50" style={{ overflow: 'visible', position: 'relative' }}>
      {/* Admin Navigation */}
      <nav className="bg-burgundy/95 backdrop-blur-md border-b border-gold-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png"
                alt="Luminelle"
                className="h-10 w-auto"
              />
              <span className="text-lg font-medium text-cream ml-3">Admin Panel</span>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-gold/20 text-gold border-gold/30">Administrator</Badge>
              <Button onClick={onLogout} variant="outline" className="border-cream text-cream hover:bg-cream/10">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-burgundy mb-2 font-serif">Admin Dashboard</h1>
            <p className="text-burgundy/70 text-lg">Manage your Luminelle platform</p>
          </div>
          <Button
            onClick={async () => {
              try {
                const response = await axios.get(`${API}/admin/debug/database`);
                console.log('=== DATABASE DEBUG RESULTS ===');
                console.log(JSON.stringify(response.data, null, 2));
                alert('Debug results logged to console. Press F12 to view.');
              } catch (error) {
                console.error('Debug error:', error);
                alert('Error running debug. Check console.');
              }
            }}
            variant="outline"
            className="text-xs"
          >
            🔍 Debug Database
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <aside className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl border border-burgundy/20 p-4 sticky top-4">
              <AdminNavigation 
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                notificationCounts={notificationCounts}
              />
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          <TabsContent value="dashboard">
            <DashboardTab stats={stats} onRefresh={fetchAdminStats} />
          </TabsContent>

          <TabsContent value="content">
            <ContentTab 
              content={content} 
              fetchContent={fetchContent} 
              categories={categories}
              setCategories={setCategories}
              refreshStats={fetchAdminStats}
            />
          </TabsContent>

          <TabsContent value="categories">
            <ContentCategoriesTab />
          </TabsContent>

          <TabsContent value="auto-tag">
            <AutoTagTool />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} removeUser={removeUser} />
          </TabsContent>

          <TabsContent value="tracking">
            <TrackingTab />
          </TabsContent>

          <TabsContent value="events">
            <EventsTab 
              events={events} 
              categories={categories}
              setCategories={setCategories}
              fetchEvents={fetchEvents} 
            />
          </TabsContent>

          <TabsContent value="marketplace">
            <MarketplaceTab 
              marketplaceItems={marketplaceItems} 
              fetchMarketplaceItems={fetchMarketplaceItems} 
            />
          </TabsContent>

          <TabsContent value="coupons">
            <CouponsTab />
          </TabsContent>

          <TabsContent value="content-boxes">
            <ContentBoxesTab />
          </TabsContent>

          <TabsContent value="faq">
            <FAQTab />
          </TabsContent>

          <TabsContent value="welcome-video">
            <HomepageCarouselTab />
          </TabsContent>

          <TabsContent value="homepage">
            <HomePageTab 
              homePageContent={homePageContent} 
              fetchHomePageContent={fetchHomePageContent}
            />
          </TabsContent>

          <TabsContent value="dosing">
            <DosingDatabaseTab />
          </TabsContent>

          <TabsContent value="image-library">
            <ImageLibraryTab />
          </TabsContent>

          <TabsContent value="moderation">
            <ModerationTab />
          </TabsContent>

          <TabsContent value="chat-rooms">
            <ChatRoomsTab />
          </TabsContent>

          <TabsContent value="community-personas">
            <CommunityPersonasTab />
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralsTab />
          </TabsContent>

          <TabsContent value="support-requests">
            <SupportRequestsTab />
          </TabsContent>

          <TabsContent value="crm">
            <CRMTab />
          </TabsContent>

          <TabsContent value="surveys">
            <SurveysTab surveys={surveys} fetchSurveys={fetchSurveys} handleExportSurveys={handleExportSurveys} />
          </TabsContent>

          <TabsContent value="polls">
            <PollsTab 
              polls={polls} 
              newPoll={newPoll}
              setNewPoll={setNewPoll}
              fetchPolls={fetchPolls}
              handleCreatePoll={handleCreatePoll}
              handleTogglePoll={handleTogglePoll}
            />
          </TabsContent>

          <TabsContent value="quizzes">
            <QuizzesTab 
              quizzes={quizzes}
              contentList={contentList}
              fetchQuizzes={fetchQuizzes}
              fetchContentForQuizzes={fetchContentForQuizzes}
              handleDeleteQuiz={handleDeleteQuiz}
            />
          </TabsContent>

            </Tabs>
          </main>
        </div>
      </div>
    </div>
  );
};


export default AdminPanel;
